<template>
    <q-expansion-item
        ref="expansionItem"
        class="RequirementAccordion"
        :class="[
            { 'RequirementAccordion--highlight': isHighlighted },
            leftStripAccentStatus && `RequirementAccordion--strip-accent-${ leftStripAccentStatus }`,
            borderAccentStatus && `RequirementAccordion--border-accent-${ borderAccentStatus }`,
        ]"
        header-class="RequirementAccordion__header"
        :model-value="expanded"
        hide-expand-icon
        @update:model-value="onExpandsionInput"
        @show="$emit('show')"
        @hide="$emit('hide')"
    >
        <template #header>
            <div class="RequirementAccordion__headerContainer">
                <div class="RequirementAccordion__iconAndTitle">
                    <div v-if="$slots.beforeTitle" @click="slotClicked">
                        <slot name="beforeTitle" />
                    </div>
                    <EidIcon
                        v-else
                        class="RequirementAccordion__typeIcon"
                        :icon-src="typeIconSrc"
                        :default-icon-svg="defaultCoverageIcon"
                    />

                    <span class="RequirementAccordion__title">{{ title }}</span>

                    <div
                        v-if="$slots.afterTitle"
                        class="flex gap-2"
                        @click="slotClicked"
                    >
                        <slot name="afterTitle" />
                    </div>
                </div>
                <div class="RequirementAccordion__rightSection">
                    <div v-if="$slots.beforeExpandToggle" @click="slotClicked">
                        <slot name="beforeExpandToggle" />
                    </div>

                    <slot name="expandToggle">
                        <div class="RequirementAccordion__expandIcon">
                            <q-icon
                                :name="expanded ? 'fal fa-chevron-up' : 'fal fa-chevron-down'"
                                size="16px"
                            />
                        </div>
                    </slot>

                    <div v-if="$slots.afterExpandToggle" @click="slotClicked">
                        <slot name="afterExpandToggle" />
                    </div>
                </div>
            </div>
        </template>

        <div class="RequirementAccordion__content">
            <slot name="content" />
        </div>
    </q-expansion-item>
</template>

<style lang="scss">
$border-radius: 8.5px;
$border-color: var(--eidSecureBlue-10);
$padding-x: 14px;
$padding-y: 10.5px;

$default-box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);

@keyframes highlightPulse {
    0% {
        box-shadow: 0 0 0 0 var(--eidTrustGreen);
    }

    50% {
        box-shadow: 0 0 0 2px var(--eidTrustGreen);
    }

    100% {
        box-shadow: $default-box-shadow;
    }
}

.RequirementAccordion {
    border: 1px solid $border-color;
    border-radius: $border-radius;
    transition: all 0.3s ease;
    background-color: var(--eidWhite);
    width: 100%;
    box-shadow: $default-box-shadow;

    &--highlight {
        animation: highlightPulse 1s ease-out;
        margin-top: 4px;
    }

    &--border-accent-success {
        border-color: var(--eidTrustGreen);
    }

    &--border-accent-warning {
        border-color: var(--eidWarningYellow);
    }

    &--border-accent-danger {
        border-color: var(--eidDangerRed);
    }

    // strip placed behind to not overriden by border accent. ideally they should be same color by status, but for now
    // the design does not allow for this for RiskRequirement, hence controlling them separately until we can uniform
    &--strip-accent-success {
        border-left: 4px solid var(--eidTrustGreen);
    }

    &--strip-accent-warning {
        border-left: 4px solid var(--eidWarningYellow);
    }

    &--strip-accent-danger {
        border-left: 4px solid var(--eidDangerRed);
    }

    &:hover {
        // border-color: $border-color;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &--inEvaluationRule:hover {
        border-left: 4px solid var(--eidTrustGreen);
    }

    &__header {
        padding: $padding-y $padding-x;
        border-top-left-radius: $border-radius;
        border-top-right-radius: $border-radius;
        background: var(--eidBgGrayTones-lightest);
    }

    .q-expansion-item--expanded &__header {
        border-bottom: 1px solid $border-color;
    }

    .q-expansion-item--collapsed &__header {
        border-bottom-left-radius: $border-radius;
        border-bottom-right-radius: $border-radius;
    }

    &__headerContainer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 12px;
    }

    &__iconAndTitle {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
        min-width: 0; // Allow text truncation if needed
    }

    &__typeIcon {
        width: 24px;
        height: 24px;
        flex-shrink: 0;

        svg {
            color: var(--eidSecureBlue-75);
        }
    }

    &__title {
        font-weight: 500;
        font-size: 16px;
        color: var(--eidDarkBlue);
    }

    &__rightSection {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-shrink: 0;
    }

    &__criteriaCount {
        font-size: 13px;
        color: var(--eidSecureBlue-75);
        white-space: nowrap;

        strong {
            font-weight: 500;
        }
    }

    &__expandIcon {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s ease;
        width: 28px;
        height: 28px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import defaultCoverageIcon from '@/modules/entity-details/assets/coverage-icon-default.svg';
    import { getRequirementTypeIconUri } from '@/modules/entity-details/utils/getRequirementTypeIconUri';

    type AccentStatus = 'success' | 'warning' | 'danger';

    export default defineComponent({
        name: 'RequirementAccordion',
        components: {
            EidIcon,
        },
        props: {
            requirementType: {
                type: [ Object, String ] as PropType<TprmRequirementType>,
                required: true,
            },
            title: {
                type: String,
                default: '',
            },
            expanded: {
                type: Boolean,
                default: false,
            },
            leftStripAccentStatus: {
                type: String as PropType<AccentStatus | undefined>,
                default: undefined,
            },
            borderAccentStatus: {
                type: String as PropType<AccentStatus | undefined>,
                default: undefined,
            },
        },
        emits: [ 'hide', 'show', 'update:expanded' ],
        data() {
            return {
                defaultCoverageIcon,
                typeIconSrc: getRequirementTypeIconUri((this as any).requirementType),
                isHighlighted: false,
            };
        },
        watch: {
            requirementType: {
                immediate: true,
                handler(newVal: TprmRequirementType) {
                    (this as any).typeIconSrc = getRequirementTypeIconUri(newVal);
                },
            },
        },
        methods: {
            slotClicked(event: MouseEvent): void {
                if ((event?.target as HTMLElement)?.closest?.('[data-stop-propagation]')) {
                    event.stopPropagation();
                }
            },
            onExpandsionInput(input: boolean | Event): void {
                // clicking on header content might propagate to this event unexpectedly, we know quasar expansion
                // should only emit boolean, hence we can ignore the other events before propagating this event up.
                if (typeof input === 'boolean') {
                    (this as any).$emit('update:expanded', input);
                }
            },
            /**
             * Public API: Trigger highlight pulse effect
             * Parent components can call this method via ref to trigger the pulse animation
             */
            triggerHighlightPulse(): void {
                (this as any).isHighlighted = true;
                setTimeout(() => {
                    (this as any).isHighlighted = false;
                }, 1000);
            },
        },
    });
</script>
